// src/services/youtubeService.js
// ✅ COMPLETELY REWRITTEN - No GAPI dependency, uses fetch directly
const API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY;
const BASE_URL = 'https://www.googleapis.com/youtube/v3';

class YouTubeService {
  constructor() {
    this.apiKey = API_KEY;
    this.isLoaded = true; // No need to load external scripts
    this.accessToken = null;
  }

  // No longer needed - kept for backward compatibility
  async loadYouTubeAPI() {
    return true;
  }

  // Set access token after Google Sign In
  setAccessToken(token) {
    this.accessToken = token;
  }

  // Helper: Make API request with retry
  async fetchAPI(url, retries = 3) {
    for (let i = 0; i < retries; i++) {
      try {
        const headers = {};
        if (this.accessToken) {
          headers['Authorization'] = `Bearer ${this.accessToken}`;
        }

        const response = await fetch(url, { headers });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(`HTTP ${response.status}: ${errorData.error?.message || response.statusText}`);
        }
        
        return await response.json();
      } catch (error) {
        if (i === retries - 1) throw error;
        console.warn(`API retry ${i + 1}/${retries}...`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
      }
    }
  }

  // Helper: Fetch video details (duration, statistics)
  async fetchVideoDetails(videoIds) {
    if (!videoIds || videoIds.length === 0) return {};
    
    try {
      const url = `${BASE_URL}/videos?part=contentDetails,statistics&id=${videoIds.join(',')}&key=${this.apiKey}`;
      const data = await this.fetchAPI(url);
      
      const detailsMap = {};
      (data.items || []).forEach(item => {
        detailsMap[item.id] = {
          duration: item.contentDetails?.duration || '',
          viewCount: item.statistics?.viewCount || '0',
          likeCount: item.statistics?.likeCount || '0',
          commentCount: item.statistics?.commentCount || '0',
        };
      });
      
      return detailsMap;
    } catch (error) {
      console.error('Video details error:', error);
      return {};
    }
  }

  // Parse ISO 8601 duration to seconds
  parseDuration(duration) {
    if (!duration) return 0;
    const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (!match) return 0;
    const hours = parseInt(match[1] || '0', 10);
    const minutes = parseInt(match[2] || '0', 10);
    const seconds = parseInt(match[3] || '0', 10);
    return hours * 3600 + minutes * 60 + seconds;
  }

  // Clean video titles
  cleanTitle(title) {
    if (!title) return 'Unknown Title';
    return title
      .replace(/\(.*?(Official\s*.*?Video|Lyrics?\s*.*?Video|Music\s*.*?Video|Audio|Visualizer|Performance).*?\)/gi, '')
      .replace(/\[.*?(Official\s*.*?Video|Lyrics?\s*.*?Video|Music\s*.*?Video|Audio|Visualizer|Performance).*?\]/gi, '')
      .replace(/\(.*?(HD|HQ|1080p|720p|4K|60fps).*?\)/gi, '')
      .replace(/\[.*?(HD|HQ|1080p|720p|4K|60fps).*?\]/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // Search for songs with pagination
  async searchSongs(query, maxResults = 20, pageToken = null) {
    try {
      let url = `${BASE_URL}/search?part=snippet&maxResults=${maxResults}&q=${encodeURIComponent(query)}&type=video&videoCategoryId=10&order=relevance&videoEmbeddable=true&key=${this.apiKey}`;
      
      if (pageToken) {
        url += `&pageToken=${pageToken}`;
      }

      const data = await this.fetchAPI(url);
      
      // Get video details for view counts
      const videoIds = (data.items || []).map(item => item.id.videoId).filter(Boolean);
      const detailsMap = await this.fetchVideoDetails(videoIds);

      return {
        items: (data.items || []).map(item => ({
          id: item.id.videoId,
          title: item.snippet.title,
          thumbnail: item.snippet.thumbnails?.medium?.url || item.snippet.thumbnails?.default?.url || '',
          channelTitle: item.snippet.channelTitle,
          channelId: item.snippet.channelId,
          publishedAt: item.snippet.publishedAt,
          description: item.snippet.description,
          viewCount: detailsMap[item.id.videoId]?.viewCount,
        })),
        nextPageToken: data.nextPageToken || null,
        prevPageToken: data.prevPageToken || null,
        totalResults: data.pageInfo?.totalResults || 0,
        resultsPerPage: data.pageInfo?.resultsPerPage || maxResults,
      };
    } catch (error) {
      console.error('Search error:', error);
      throw error;
    }
  }

  // Get trending music
  async getTrendingMusic(maxResults = 20) {
    try {
      const url = `${BASE_URL}/videos?part=snippet,contentDetails,statistics&chart=mostPopular&maxResults=${maxResults}&videoCategoryId=10&regionCode=IN&key=${this.apiKey}`;
      const data = await this.fetchAPI(url);

      return (data.items || []).map(item => ({
        id: item.id,
        title: item.snippet.title,
        thumbnail: item.snippet.thumbnails?.medium?.url || item.snippet.thumbnails?.default?.url || '',
        channelTitle: item.snippet.channelTitle,
        channelId: item.snippet.channelId,
        viewCount: item.statistics?.viewCount,
        likeCount: item.statistics?.likeCount,
        publishedAt: item.snippet.publishedAt,
      }));
    } catch (error) {
      console.error('Trending error:', error);
      throw error;
    }
  }

  // Get related songs
  async getRelatedSongs(videoId, maxResults = 20) {
    try {
      const url = `${BASE_URL}/search?part=snippet&relatedToVideoId=${videoId}&type=video&maxResults=${maxResults}&videoCategoryId=10&videoEmbeddable=true&regionCode=IN&key=${this.apiKey}`;
      const data = await this.fetchAPI(url);

      const videoIds = (data.items || []).map(item => item.id?.videoId).filter(Boolean);
      const detailsMap = await this.fetchVideoDetails(videoIds);

      return (data.items || [])
        .filter(item => {
          const vidId = item.id?.videoId;
          const details = detailsMap[vidId];
          if (!details) return false;
          if (item.snippet?.liveBroadcastContent === 'live') return false;
          
          const totalSeconds = this.parseDuration(details.duration);
          if (totalSeconds === 0 || totalSeconds > 900) return false;
          
          return true;
        })
        .map(item => ({
          id: item.id.videoId,
          title: this.cleanTitle(item.snippet?.title || ''),
          thumbnail: item.snippet?.thumbnails?.medium?.url || item.snippet?.thumbnails?.default?.url || '',
          channelTitle: item.snippet?.channelTitle || 'Unknown',
          channelId: item.snippet?.channelId || '',
          viewCount: detailsMap[item.id.videoId]?.viewCount,
          likeCount: detailsMap[item.id.videoId]?.likeCount,
          publishedAt: item.snippet?.publishedAt,
          description: item.snippet?.description || '',
        }));
    } catch (error) {
      console.error('Related songs error:', error);
      return [];
    }
  }
}

export default new YouTubeService();